import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Wand2,
  LayoutDashboard,
  Lightbulb,
  Keyboard,
  Check,
  Compass,
} from 'lucide-react';
import { OnboardingFlow } from '@/components/ui/OnboardingFlow';
import { useGuideTour } from '@/context/GuideTourContext';

const STORAGE_KEY = 'khiimori.onboarded';

/**
 * FirstRunGate — shows the zero-config onboarding flow the first time
 * a user lands on a hub root. Subsequent visits are silent.
 *
 * Three steps:
 *  1. "Open the browser extension and load a few tabs" — copy-only tip.
 *  2. "Pick a starting action" — three large buttons that take the
 *     user to the right hub.
 *  3. "Press Cmd+K any time" — copy + closes the flow.
 *
 * The flow is dismissable via the X button (sets the same flag, so it
 * doesn't reappear on next visit).
 */
export function FirstRunGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { startTour } = useGuideTour();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let alreadyOnboarded = false;
    try {
      alreadyOnboarded = window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      // ignore
    }
    if (alreadyOnboarded) {
      setOpen(false);
      return;
    }
    // Only trigger on the home hub root, not on legacy redirects.
    if (location.pathname === '/home' && !location.search.includes('firstRun=0')) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [location.pathname, location.search]);

  const complete = (launchTour = false) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    setOpen(false);
    if (launchTour) {
      setTimeout(() => startTour(0), 150);
    }
  };

  return (
    <OnboardingFlow
      isOpen={open}
      onComplete={() => complete(true)}
      onSkip={() => complete(false)}
      steps={[
        {
          title: 'Welcome to Khiimori',
          description:
            'Your browser tabs, organized and turned into apps by AI. Take 30 seconds to set the basics.',
          icon: <Sparkles className="h-7 w-7 text-primary-300" />,
          tips: [
            'Install the Khiimori browser extension to start tracking your tabs.',
            'Everything happens locally first — your data stays yours.',
            'You can change any of this later in Settings.',
          ],
        },
        {
          title: 'Pick where to start',
          description:
            'You can do any of these any time. We just want to know what to put on your home screen.',
          icon: <Wand2 className="h-7 w-7 text-primary-300" />,
          tips: [
            'Generate an app from your open tabs — for makers and researchers.',
            'Organize my tabs — for people drowning in too many tabs.',
            'Ask the AI — for people who just want to chat with their browser.',
          ],
        },
        {
          title: 'Press Cmd+K any time',
          description:
            'That is the single shortcut that finds every feature. Type a few letters, hit Enter, and you are there.',
          icon: <Keyboard className="h-7 w-7 text-primary-300" />,
          tips: [
            'Cmd+K (or Ctrl+K) opens the command palette.',
            'Type a skill name, a hub name, or a page name — fuzzy search handles the rest.',
            'Your last five commands appear at the top for one-tap re-runs.',
          ],
        },
        {
          title: 'Explore with an Interactive Guide Tour',
          description:
            'Rather than static slides, you can now take a live walkthrough that highlights every key part of Khiimori with interactive spotlights.',
          icon: <Compass className="h-7 w-7 text-primary-300" />,
          tips: [
            'Spotlights active tabs, AI intelligence, and app generation live.',
            'Navigate with Left/Right arrow keys or Next/Back buttons.',
            'You can replay this tour anytime from the sidebar or Settings.',
          ],
        },
      ]}
    />
  );
}

/**
 * Helper hook used by Hub "Get started" buttons to mark the flow as complete
 * and route the user to the right hub.
 */
export function usePickStartingAction() {
  const navigate = useNavigate();
  return (action: 'generate' | 'organize' | 'ai') => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    if (action === 'generate') navigate('/apps/generate');
    else if (action === 'organize') navigate('/tabs');
    else navigate('/ai');
  };
}
